import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { sequencesApi } from "@/api";
import type { Sequence, SequenceStatus } from "@/types";
import { SEQUENCE_TRIGGER_LABELS, SEQUENCE_STATUS_LABELS } from "@/types";
import { format } from "date-fns";
import { Plus, GitBranch } from "lucide-react";

const statusColors: Record<SequenceStatus, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PAUSED: "bg-slate-100 text-slate-700",
  ARCHIVED: "bg-red-100 text-red-700",
};

export default function Sequences() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["sequences"],
    queryFn: () => sequencesApi.list(),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">シーケンス</h2>
        <button
          onClick={() => navigate("/sequences/new")}
          className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
        >
          <Plus size={16} />
          新規シーケンス
        </button>
      </div>

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : !data?.data.length ? (
        <div className="text-center py-12 text-slate-400">
          <GitBranch size={48} className="mx-auto mb-4 opacity-50" />
          <p>シーケンスがありません</p>
          <p className="text-sm mt-1">「新規シーケンス」ボタンから作成してください</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">名前</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">トリガー</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">ステップ</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">登録者</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">ステータス</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">更新日</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.data.map((seq: Sequence) => (
                <tr
                  key={seq.id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/sequences/${seq.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium">{seq.name}</div>
                    {seq.description && (
                      <div className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{seq.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {SEQUENCE_TRIGGER_LABELS[seq.triggerType]}
                  </td>
                  <td className="px-4 py-3 text-center">{seq._count?.steps ?? 0}</td>
                  <td className="px-4 py-3 text-center">{seq._count?.enrollments ?? 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[seq.status]}`}>
                      {SEQUENCE_STATUS_LABELS[seq.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500">
                    {format(new Date(seq.updatedAt), "MM/dd HH:mm")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
